import { Video, VideoPDF } from "./models/Video";
import VideoEvents from "./VideoEvents";
import { VideoReaction } from "./models/VideoReaction";
import { AppAction } from "../../root/AppAction";
import { Tag } from "./models/VideoFilter";
import ExVideoEvents from "../../store/events/ExVideoEvents";
import { VideoCaption } from "../../models/video";
import SlideEvent from "../../store/events/SlideEvent";
import { FilterTag } from "../../viewer/search/Search";

export interface VideoStore {
    list: number[];
    videos: { [id: number]: Video };
    tags: Tag[];
    contents: any[];
    isLoading: boolean;
    isUploading: boolean;
    isSendVideo: boolean;
    isSuccessSendVideo: boolean;
    isFailedSendVideo: boolean;
    isEditVideo: boolean;
    isSuccessEditVideo: boolean;
    isFailedEditVideo: boolean;
    isDeleteVideo: boolean;
    isUploadDocument: boolean;
    isSuccessUploadDocument: boolean;
    isFailedUploadDocument: boolean;
    isSuccessDeleteVideo: boolean;
    isFailedDeleteVideo: boolean;
    video: Video | undefined;
    isResetVideo: boolean;
    isResetVideos: boolean;
    videoPdf: VideoPDF | undefined;
    isEditSlide: boolean;
    isContentLoading: boolean;
    filterTags: FilterTag[];
    tagDuplicateError: string;
    mediaRecorderVideoFile: File | undefined;
}

const initialStore: VideoStore = {
    list: [],
    videos: {},
    tags: [],
    contents: [],
    isLoading: true,
    isUploading: false,
    isSendVideo: false,
    isSuccessSendVideo: false,
    isFailedSendVideo: false,
    isEditVideo: false,
    isSuccessEditVideo: false,
    isUploadDocument: false,
    isFailedEditVideo: false,
    isSuccessUploadDocument: false,
    isFailedUploadDocument: false,
    isDeleteVideo: false,
    isSuccessDeleteVideo: false,
    isFailedDeleteVideo: false,
    video: undefined,
    isResetVideo: true,
    isResetVideos: true,
    videoPdf: undefined,
    isEditSlide: false,
    isContentLoading: true,
    filterTags: [],
    tagDuplicateError: "",
    mediaRecorderVideoFile: undefined,
};

function byIdMap(videos: Video[]): { [id: number]: Video } {
    return Object.fromEntries(videos.map((video) => [video.id, video]));
}

function UpdateCaption(defaultCaption: any, updateCaption: VideoCaption) {
    if (defaultCaption) {
        const newCaptionList: VideoCaption[] = defaultCaption;
        const editCaptionIndex: number = newCaptionList.findIndex(
            (caption) => caption.id === updateCaption.id
        );
        newCaptionList[editCaptionIndex] = updateCaption;
        return newCaptionList;
    }
}

export default function video(state: VideoStore = initialStore, action: AppAction) {
    switch (action.type) {
        case VideoEvents.LoadVideoListStarted:
            return {
                ...state,
                isLoading: true,
            };

        case VideoEvents.LoadVideoListComplete:
            return {
                ...state,
                isLoading: false,
                videos: byIdMap(action.videos),
                list: action.videos.map(({ id }: Video) => id),
                isResetVideos: false,
            };

        case VideoEvents.LoadVideoStarted:
            return { ...state, isLoading: true };

        case VideoEvents.LoadVideoComplete:
            return {
                ...state,
                videos: {
                    ...state.videos,
                    [action.video.id]: action.video,
                },
                video: action.video,
                list: [...state.list, action.video.id],
                isLoading: false,
                isResetVideo: false,
            };

        case VideoEvents.CreateStarted:
            return {
                ...state,
                isUploading: true,
            };

        case VideoEvents.CreateComplete:
            return {
                ...state,
                isUploading: false,
            };

        case VideoEvents.IncrementViewCount:
            return {
                ...state,
                videos: {
                    ...state.videos,
                    [action.videoId]: {
                        ...state.videos[action.videoId],
                        viewCount: state.videos[action.videoId].viewCount + 1,
                    },
                },
            };

        case VideoEvents.Update:
            return {
                ...state,
                videos: {
                    ...state.videos,
                    [action.videoId]: {
                        ...state.videos[action.videoId],
                        ...action.changeSet,
                    },
                },
            };

        // コンテンツのコメント
        case VideoEvents.AddVideoCommentComplete:
            return {
                ...state,
                videos: {
                    ...state.videos,
                    [action.comment.videoId]: {
                        ...state.videos[action.comment.videoId],
                        comments: action.comment,
                    }
                },
                video: {
                    ...state.video,
                    comments: action.comment,
                }
            };

        // コメントの更新
        case VideoEvents.UpdateVideoCommentComplete:
            return {
                ...state,
                videos: {
                    ...state.videos,
                    [action.videoId]: {
                        ...state.videos[action.videoId],
                        comments: action.videoCommentChangeSet,
                    }
                },
                video: {
                    ...state.video,
                    comments: action.videoCommentChangeSet,
                }
            };

        // コメントの削除
        case VideoEvents.DeleteVideoCommentComplete:
            return {
                ...state,
                videos: {
                    ...state.videos,
                    [action.videoId]: {
                        ...state.videos[action.videoId],
                        comments: action.videoCommentChangeSet,
                    }
                },
                video: {
                    ...state.video,
                    comments: action.videoCommentChangeSet,
                }
            };

        case VideoEvents.ReactComplete:
            return {
                ...state,
                videos: {
                    ...state.videos,
                    [action.reaction.videoId]: {
                        ...state.videos[action.reaction.videoId],
                        reactions: [
                            ...state.videos[action.reaction.videoId].reactions,
                            action.reaction,
                        ].sort(reactionTimeComparator),
                    },
                },
            };

        case VideoEvents.Delete:
            return {
                ...state,
                videos: Object.fromEntries(
                    Object.entries(state.videos).filter(([_, { id }]) => id !== action.videoId)
                ),
                list: state.list.filter((id) => id !== action.videoId),
            };

        case VideoEvents.UploadExtraMaterialsComplete:
            return {
                ...state,
                videos: {
                    ...state.videos,
                    [action.material.videoId]: {
                        ...state.videos[action.material.videoId],
                        extraMaterials: [
                            ...state.videos[action.material.videoId].extraMaterials,
                            action.material,
                        ],
                    },
                },
            };

        case VideoEvents.DeleteExtraMaterials:
            return {
                ...state,
                videos: {
                    ...state.videos,
                    [action.videoId]: {
                        ...state.videos[action.videoId],
                        extraMaterials: state.videos[action.videoId].extraMaterials.filter(
                            ({ id }) => id !== action.materialId
                        ),
                    },
                },
            };

        case VideoEvents.filterVideoListStarted:
            return {
                ...state,
                isContentLoading: true,
                contents: [],
            };

        case VideoEvents.filterVideoListComplete:
            return {
                ...state,
                isContentLoading: false,
                videos: byIdMap(action.filterResult.videos),
                list: action.filterResult.videos.map(({ id }: Video) => id),
                filterTags: action.filterResult.tags,
                contents: action.filterResult.contents,
                isResetVideos: false,
            };
        case VideoEvents.createNewTagStarted:
            return {
                ...state,
                tagDuplicateError: "",
            };
        case VideoEvents.createNewTagCompleted:
            return {
                ...state,
                tags: action.tagOutput,
            };
        case VideoEvents.createNewTagFailed:
            return {
                ...state,
                tags: action.tagOutput,
                tagDuplicateError: action.message,
            };
        case VideoEvents.resetDuplicateError:
            return {
                ...state,
                tagDuplicateError: "",
            };

        case VideoEvents.getTagListStarted:
            return {
                ...state,
            };

        case VideoEvents.getTagListComplete:
            return {
                ...state,
                tags: action.tagOutput,
            };

        // ビデオ追加
        case VideoEvents.AddVideoStarted:
            return {
                ...state,
                isSendVideo: true,
                isSuccessSendVideo: false,
                isFailedSendVideo: false,
            };

        case VideoEvents.AddVideoComplete:
            return {
                ...state,
                isSendVideo: false,
                isSuccessSendVideo: true,
                isFailedSendVideo: false,
            };

        case VideoEvents.AddVideoFailed:
            return {
                ...state,
                isSendVideo: false,
                isSuccessSendVideo: false,
                isFailedSendVideo: true,
            };

        // ビデオ編集
        case VideoEvents.EditVideoStarted:
            return {
                ...state,
                isLoading: true,
                isEditVideo: true,
                isSuccessEditVideo: false,
                isFailedEditVideo: false,
                video: undefined,
            };

        case VideoEvents.EditVideoComplete:
            return {
                ...state,
                videos: {
                    ...state.videos,
                    [action.updateVideo.id]: action.updateVideo,
                },
                video: action.updateVideo,
                isLoading: false,
                isEditVideo: false,
                isSuccessEditVideo: true,
                isFailedEditVideo: false,
            };
        case VideoEvents.EditVideoFailed:
            return {
                ...state,
                isLoading: false,
                isEditVideo: false,
                isSuccessEditVideo: false,
                isFailedEditVideo: true,
            };

        // ビデオ資料アップロード
        case VideoEvents.UploadDocumentStarted:
            return {
                ...state,
                isLoading: true,
                isUploadDocument: true,
                isSuccessEditVideo: false,
                isFailedEditVideo: false,
            };

        case VideoEvents.UploadDocumentComplete:
            return {
                ...state,
                isLoading: false,
                isUploadDocument: false,
                isSuccessUploadDocument: true,
                videos: {
                    ...state.videos,
                    [action.document.id]: {
                        ...state.videos[action.document.id],
                        extraMaterials: action.document.extraMaterials,
                    },
                },
                video: {
                    ...state.video,
                    extraMaterials: action.document.extraMaterials,
                },
            };
        case VideoEvents.UploadDocumentFailed:
            return {
                ...state,
                isLoading: false,
                isUploadDocument: false,
                isFailedUploadDocument: true,
            };

        // ビデオ削除
        case VideoEvents.DeleteVideoStarted:
            return {
                ...state,
                isLoading: true,
                isDeleteVideo: true,
                isSuccessDeleteVideo: false,
                isFailedDeleteVideo: false,
            };

        case VideoEvents.DeleteVideoComplete:
            return {
                ...state,
                isLoading: false,
                isDeleteVideo: false,
                isSuccessDeleteVideo: true,
                isFailedDeleteVideo: false,
                list: state.list.filter((id) => id !== action.videoId),
                contents: state.contents.filter(
                    (content) =>
                        content.fileType || (!content.fileType && content.id !== action.videoId)
                ),
            };

        case VideoEvents.DeleteVideoFailed:
            return {
                ...state,
                isLoading: false,
                isDeleteVideo: false,
                isSuccessDeleteVideo: false,
                isFailedDeleteVideo: true,
            };

        // STT修正でvideoストア更新
        case ExVideoEvents.EditVideoCaptionComplete: {
            return {
                ...state,
                video: {
                    ...state.video,
                    captions: UpdateCaption(state.video?.captions, action.updateCaption),
                },
            };
        }

        // ビデオデータリセット
        case VideoEvents.ResetVideos:
            return {
                ...state,
                list: [],
                videos: {},
                isLoading: true,
                isResetVideos: true,
            };
        case VideoEvents.ResetVideo:
            return {
                ...state,
                isResetVideo: true,
                isLoading: false,
                video: undefined,
                isDeleteVideo: false,
                isSuccessDeleteVideo: false,
                isFailedDeleteVideo: false,
            };

        // ビデオスライド資料取得
        case VideoEvents.GetVideoSlide:
            return {
                ...state,
                isLoading: true,
            };

        case VideoEvents.GetVideoSlideComplete:
            return {
                ...state,
                isLoading: false,
                videoPdf: action.videoPdf,
            };

        case VideoEvents.SetMediaRecorderVideoFile:
            return {
                ...state,
                mediaRecorderVideoFile: action.videoFile,
            };

        case VideoEvents.DeleteMediaRecorderVideoFile:
            return {
                ...state,
                mediaRecorderVideoFile: undefined,
            };

            

        // コンテンツの編集
        case SlideEvent.EditSlideStarted:
            return {
                ...state,
                isLoading: true,
                isEditSlide: true,
                isSuccessEditSlide: false,
                isFailedEditSlide: false,
            };

        case SlideEvent.EditSlideComplete:
            return {
                ...state,
                isLoading: false,
                isEditSlide: false,
                isSuccessEditSlide: true,
                isFailedEditSlide: false,
            };

        case SlideEvent.EditSlideFailed:
            return {
                ...state,
                isLoading: false,
                isEditSlide: false,
                isSuccessEditSlide: false,
                isFailedEditSlide: true,
            };

        // コンテンツの削除
        case SlideEvent.DeleteSlideComplete:
            return {
                ...state,
                contents: state.contents.filter(
                    (content) => !content.fileType || (content.fileType && content.id !== action.id)
                ),
            };

        default:
            return state;
    }
}

function reactionTimeComparator(r1: VideoReaction, r2: VideoReaction) {
    return r1.reactionTimeMillis - r2.reactionTimeMillis;
}
